import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CountryCode } from 'libphonenumber-js/max';
import { DEFAULT_PHONE_COUNTRY, PHONE_COUNTRIES, PhoneCountry, getPhoneCountry } from '../../data/phone-countries.data';

@Component({
  selector: 'app-phone-country-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './phone-country-select.component.html',
  styleUrls: ['./phone-country-select.component.scss']
})
export class PhoneCountrySelectComponent {
  @Input() id = 'phoneCountrySelect';
  @Input() disabled = false;
  @Output() countryChange = new EventEmitter<CountryCode>();

  countries: PhoneCountry[] = PHONE_COUNTRIES;
  isOpen = false;
  search = '';
  /** Índice (em filteredCountries) do item destacado pelo teclado. */
  activeIndex = 0;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('toggleButton') toggleButton?: ElementRef<HTMLButtonElement>;

  private _country: CountryCode = DEFAULT_PHONE_COUNTRY;

  @Input()
  set country(value: CountryCode) {
    this._country = value || DEFAULT_PHONE_COUNTRY;
  }
  get country(): CountryCode {
    return this._country;
  }

  get selectedCountry(): PhoneCountry {
    return getPhoneCountry(this._country);
  }

  get filteredCountries(): PhoneCountry[] {
    const term = this.search.trim().toLowerCase();
    if (!term) {
      return this.countries;
    }
    return this.countries.filter(c =>
      c.name.toLowerCase().includes(term) ||
      c.dialCode.includes(term) ||
      c.iso2.toLowerCase().includes(term)
    );
  }

  constructor(private _elementRef: ElementRef) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this._elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.close();
      this.toggleButton?.nativeElement.focus();
    }
  }

  /** Setas navegam na lista, Enter seleciona o item destacado (Esc é tratado em onEscape). */
  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.open();
      }
      return;
    }

    const total = this.filteredCountries.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1, total);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1, total);
        break;
      case 'Tab':
        // Foca o botão antes de fechar (o foco não se perde ao remover o painel) e deixa o Tab/Shift+Tab seguir dali
        this.toggleButton?.nativeElement.focus();
        this.close();
        break;
      case 'Enter':
        event.preventDefault();
        if (total) {
          this.select(this.filteredCountries[this.activeIndex] || this.filteredCountries[0]);
        }
        break;
    }
  }

  onSearchChange(): void {
    this.activeIndex = 0;
    this.scrollActiveIntoView();
  }

  get listboxId(): string {
    return `${this.id}-listbox`;
  }

  optionId(country: PhoneCountry): string {
    return `${this.id}-option-${country.iso2}`;
  }

  get activeOptionId(): string | null {
    const active = this.filteredCountries[this.activeIndex];
    return active ? this.optionId(active) : null;
  }

  get toggleLabel(): string {
    return `Selecionar país, atual: ${this.selectedCountry.name} +${this.selectedCountry.dialCode}`;
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    this.isOpen = true;
    this.search = '';
    const selectedIndex = this.countries.findIndex(c => c.iso2 === this._country);
    this.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
    // O campo de busca só existe depois da renderização do painel
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      this.doScrollActiveIntoView();
    });
  }

  close(): void {
    this.isOpen = false;
  }

  select(country: PhoneCountry): void {
    this._country = country.iso2;
    this.countryChange.emit(country.iso2);
    this.close();
    this.toggleButton?.nativeElement.focus();
  }

  private moveActive(step: number, total: number): void {
    if (!total) {
      return;
    }
    this.activeIndex = (this.activeIndex + step + total) % total;
    this.scrollActiveIntoView();
  }

  /** Espera a detecção de mudanças atualizar a classe "active" antes de rolar até o item. */
  private scrollActiveIntoView(): void {
    setTimeout(() => this.doScrollActiveIntoView());
  }

  private doScrollActiveIntoView(): void {
    const list: HTMLElement | null = this._elementRef.nativeElement.querySelector('.phone-country-select__list');
    const item: HTMLElement | null = list ? list.querySelector('.phone-country-select__item.active') : null;
    if (item) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }
}
